import { STAFF_EMAIL_DOMAIN } from "@rmh/defs";

/**
 * Whether an address belongs to staff, and so should be sent to SSO instead of
 * issued a magic link.
 *
 * Matches the domain exactly rather than by suffix: `endsWith("@...clinic")`
 * would be equivalent here, but a bare `endsWith(".clinic")` would also accept
 * `someone@not-us.clinic`, and splitting on the last `@` makes that class of
 * mistake impossible rather than merely absent.
 *
 * A plus-addressed staff-domain email (`name+test@rightmovehealth.clinic`) is
 * not staff: it is a Staff Test Account, a patient account staff keep for
 * using the patient app as a patient, and it signs in by magic link. Only the
 * bare address is routed to SSO.
 *
 * This only decides which sign-in flow to offer. It grants nothing — the
 * account still has to authenticate, and the middleware still reads the real
 * Cognito groups before letting anyone near the patient picker. A patient who
 * somehow held a bare staff-domain address would be sent to SSO and fail there.
 */
export const isStaffEmail = (email: string | undefined | null): boolean => {
  if (!email) {
    return false;
  }

  const trimmed = email.trim().toLowerCase();
  const atIndex = trimmed.lastIndexOf("@");

  if (atIndex === -1) {
    return false;
  }

  const localPart = trimmed.slice(0, atIndex);

  return (
    trimmed.slice(atIndex + 1) === STAFF_EMAIL_DOMAIN &&
    !localPart.includes("+")
  );
};
